import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { boardQueryOptions } from "@/lib/board.queries";
import { aiaQueryOptions } from "@/lib/aia.queries";
import { sponsorsQueryOptions } from "@/lib/sponsors.queries";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck,
  Award,
  Handshake,
  Newspaper,
  FileText,
  Mail,
  Mic,
  Calendar,
  ArrowRight,
  Activity,
  Images,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/")({
  component: Dashboard,
});

function Dashboard() {
  const board = useQuery(boardQueryOptions());
  const aia = useQuery(aiaQueryOptions());
  const sponsors = useQuery(sponsorsQueryOptions());

  const aiaActive = (aia.data ?? []).filter((m) => m.is_active).length;
  const sponsorsActive = (sponsors.data ?? []).filter((s) => s.is_active).length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Welcome back</h2>
        <p className="text-sm text-muted-foreground">
          Snapshot of your site. Live counts come from the database — other widgets are demo placeholders.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={<ShieldCheck className="h-4 w-4" />} label="Board members" value={board.data?.length ?? 0} hint="Live" to="/admin/board" />
        <Kpi icon={<Award className="h-4 w-4" />} label="Advisors (active)" value={`${aiaActive} / ${aia.data?.length ?? 0}`} hint="Live" to="/admin/advisors" />
        <Kpi icon={<Handshake className="h-4 w-4" />} label="Sponsors" value={`${sponsorsActive} / ${sponsors.data?.length ?? 0}`} hint="Live" to="/admin/sponsors" />
        <Kpi icon={<Newspaper className="h-4 w-4" />} label="Articles" value="—" hint="Demo" to="/admin/news" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={<Images className="h-4 w-4" />} label="Hero slideshow" value="Manage" hint="Live" to="/admin/slideshow" />
        <Kpi icon={<FileText className="h-4 w-4" />} label="Research reports" value="—" hint="Demo" to="/admin/research" />
        <Kpi icon={<Mic className="h-4 w-4" />} label="Show episodes" value="—" hint="Demo" to="/admin/beauty-show" />
        <Kpi icon={<Calendar className="h-4 w-4" />} label="Upcoming events" value="—" hint="Demo" to="/admin/events" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="h-4 w-4" /> Recent activity
            </CardTitle>
            <CardDescription>Editorial & content edits feed (demo).</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y text-sm">
              {[
                { who: "nnolasco", what: "Updated 4 board member bios", when: "2 hours ago" },
                { who: "system", what: "Imported 106 AIA advisors", when: "yesterday" },
                { who: "nnolasco", what: "Replaced hero slide on Home v3 (Luxe)", when: "2 days ago" },
                { who: "nnolasco", what: "Published article: \"Trends Shaping Aesthetic Medicine\"", when: "3 days ago" },
              ].map((row, i) => (
                <li key={i} className="flex items-center justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <p className="truncate">{row.what}</p>
                    <p className="text-xs text-muted-foreground">by {row.who}</p>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">{row.when}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Quick actions</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2">
            <QuickAction to="/admin/board" label="Manage board" />
            <QuickAction to="/admin/advisors" label="Manage advisors" />
            <QuickAction to="/admin/sponsors" label="Add sponsor" />
            <QuickAction to="/admin/slideshow" label="Edit hero slideshow" />
            <QuickAction to="/admin/news" label="Draft an article" />
            <QuickAction to="/admin/templates" label="Switch site template" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Kpi({
  icon,
  label,
  value,
  hint,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  hint: string;
  to: string;
}) {
  return (
    <Link to={to}>
      <Card className="transition-colors hover:bg-muted/40">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between text-muted-foreground">
            <span className="text-xs uppercase tracking-wider">{label}</span>
            {icon}
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-semibold tracking-tight">{value}</div>
          <Badge variant={hint === "Live" ? "default" : "secondary"} className="mt-2 text-[10px]">
            {hint}
          </Badge>
        </CardContent>
      </Card>
    </Link>
  );
}

function QuickAction({ to, label }: { to: string; label: string }) {
  return (
    <Button asChild variant="outline" size="sm" className="justify-between">
      <Link to={to}>
        {label} <ArrowRight className="h-4 w-4" />
      </Link>
    </Button>
  );
}
